import type { Maneuver } from './maneuvers';
import type { DisplayLine } from './osrmMatch';
import type { SpeedLimitSpan } from './speedLimits';

export type Role = 'participant' | 'staff' | 'admin';

export type Profile = {
  id: string;
  display_name: string;
  car: string | null;
  role: Role;
  /** Compte validé par un organisateur (sinon « en attente ») */
  approved: boolean;
  /** adresse e-mail confirmée (lien cliqué) ; absent des anciennes versions */
  email_confirmed?: boolean;
  /** langue du téléphone, pour les notifications envoyées par la base */
  language?: 'fr' | 'en';
};

/**
 * Demande d'adhésion à traiter par le staff : participant pas encore validé, dont l'adresse e-mail
 * est confirmée (avant, ce n'est qu'une inscription commencée).
 */
export const isPendingMember = (p: Pick<Profile, 'approved' | 'role' | 'email_confirmed'>) =>
  !p.approved && p.role === 'participant' && p.email_confirmed !== false;

/** Réglages du membre (Profil → Réglages), appliqués par la base. */
export type MemberSettings = {
  /** notification à la création d'un événement */
  notify_events: boolean;
  /** notification des messages de discussion */
  notify_chat: boolean;
  /** staff : notification des demandes d'adhésion */
  notify_members: boolean;
  /** position visible des autres participants pendant un rallye (le staff la voit toujours) */
  share_position: boolean;
};

/** Points stockés en base : [[lat, lon], ...] */
export type DbPoints = [number, number][];

export type RouteRow = {
  id: string;
  name: string;
  points: DbPoints;
  distance_m: number;
  source: 'import' | 'recorded';
  created_at: string;
  /** Instructions de guidage calculées (null = pas encore calculées) */
  maneuvers?: Maneuver[] | null;
  maneuvers_source?: string | null;
  /** Limitations de vitesse le long du tracé (null = pas encore calculées) */
  speed_limits?: SpeedLimitSpan[] | null;
  /** Tracé recalé sur les routes, pour le dessin seulement (null = tracé brut) */
  display_line?: DisplayLine | null;
};

export type RouteSummary = Omit<RouteRow, 'points' | 'maneuvers' | 'speed_limits' | 'display_line'>;

/** rally : avec tracé ; meet : rassemblement (lieu + heures, sans tracé) */
export type EventKind = 'rally' | 'meet';

export type EventRow = {
  id: string;
  title: string;
  description: string | null;
  event_date: string; // YYYY-MM-DD
  kind: EventKind;
  start_time: string | null; // HH:MM:SS
  end_time: string | null; // HH:MM:SS
  meeting_point: string | null;
  meeting_lat: number | null;
  meeting_lon: number | null;
  route_id: string | null;
  /** false = brouillon, visible seulement par le staff (migration 0024) */
  published?: boolean;
  interested_count: number;
};

export type PrivateInfo = {
  first_name: string | null;
  last_name: string | null;
  /** « Prénom Nom », pour l'affichage côté organisateurs */
  full_name: string | null;
  license_plate: string | null;
};

/** Statut dans un événement : inscrit -> actif (parti) -> a participé (trajet enregistré). */
export type ParticipationStatus = 'registered' | 'active' | 'done';
export type Participant = Profile & { status: ParticipationStatus };

/** Résumé d'un trajet terminé (historique). */
export type TripRecord = {
  id: string;
  user_id: string;
  event_id: string | null;
  event_title: string;
  event_date: string; // YYYY-MM-DD
  started_at: string;
  ended_at: string;
  distance_m: number;
  moving_time_s: number;
  max_speed_kmh: number;
  avg_speed_kmh: number;
  /** part du tracé réellement suivie (0-100) */
  completed_pct: number | null;
  /** distance roulée hors parcours (m) ; null pour les trajets enregistrés avant la version 1.6 */
  off_route_m: number | null;
  points: [number, number][];
  created_at: string;
};

export type ChatMessage = {
  id: string;
  event_id: string;
  user_id: string;
  body: string | null;
  /** Chemin de la photo dans le bucket « chat » */
  image_path: string | null;
  created_at: string;
  /** annonce du staff : lue à la voix dans les voitures en route */
  announcement?: boolean;
};

export type LivePosition = {
  event_id: string;
  user_id: string;
  lat: number;
  lon: number;
  heading: number | null;
  speed: number | null;
  off_route: boolean;
  updated_at: string;
};
